import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Mail, Phone, MapPin, Clock, Send, CheckCircle, AlertCircle } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import {
  SUPPORT_EMAIL,
  PRIVACY_EMAIL,
  COMPANY_NAME,
  BRAND_NAME,
  WEBSITE_URL,
  SUPPORT_PHONE,
  SUPPORT_PHONE_TEL,
  SUPPORT_HOURS,
  COMPANY_ADDRESS_LINE1,
  COMPANY_ADDRESS_LINE2,
} from '../constants/contact'
import { Logo } from '../components/Logo'

export default function Contact() {
  const { user } = useAuth()
  const [name, setName] = useState('')
  const [email, setEmail] = useState(user?.email || '')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [honeypot, setHoneypot] = useState('')
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (status === 'sending') return
    setStatus('sending')
    try {
      const res = await fetch('/.netlify/functions/send-contact-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, subject, message, website: honeypot }),
      })
      if (!res.ok) throw new Error('send failed')
      setStatus('sent')
      setSubject('')
      setMessage('')
    } catch {
      setStatus('error')
    }
  }

  return (
    <div className="min-h-screen bg-paybyphone-light">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to={user ? '/dashboard' : '/'} className="flex items-center">
            <Logo width={150} height={36} />
          </Link>
          {user && (
            <Link to="/dashboard" className="text-paybyphone-green hover:text-paybyphone-green/80">
              ← Back to Dashboard
            </Link>
          )}
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Contact Us / Contactez-nous</h1>
          <p className="text-gray-600 mb-8">
            Reach out to our team — we typically respond within one business day.
            <br />
            Écrivez-nous — nous répondons généralement sous un jour ouvré.
          </p>

          <div className="grid gap-8 md:grid-cols-2">
            {/* Contact form */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Send us a message</h2>
              {status === 'sent' ? (
                <div className="bg-green-50 border border-green-200 rounded-xl p-6 flex items-start">
                  <CheckCircle className="w-6 h-6 text-paybyphone-green mr-3 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">Message sent!</p>
                    <p className="text-gray-600 text-sm mt-1">
                      Thank you — our support team will get back to you shortly at {email}.
                    </p>
                    <button
                      onClick={() => setStatus('idle')}
                      className="text-paybyphone-green text-sm font-medium mt-3 hover:underline"
                    >
                      Send another message
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Honeypot anti-spam (doit rester vide) */}
                  <input
                    type="text"
                    name="website"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    className="hidden"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                  />
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-gray-700 mb-1">
                      Name / Nom *
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      required
                      maxLength={200}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-paybyphone-green"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-paybyphone-green"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-gray-700 mb-1">
                      Subject / Sujet
                    </label>
                    <input
                      id="contact-subject"
                      type="text"
                      maxLength={200}
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-paybyphone-green"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-gray-700 mb-1">
                      Message *
                    </label>
                    <textarea
                      id="contact-message"
                      required
                      rows={5}
                      maxLength={5000}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-paybyphone-green"
                    />
                  </div>
                  {status === 'error' && (
                    <div className="flex items-center text-red-600 text-sm">
                      <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                      Something went wrong. Please try again or email us at {SUPPORT_EMAIL}.
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={status === 'sending'}
                    className="btn-primary inline-flex items-center px-6 py-3 disabled:opacity-60"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {status === 'sending' ? 'Sending…' : 'Send message'}
                  </button>
                </form>
              )}
            </div>

            {/* Contact details */}
            <div className="space-y-4">
              <div className="border border-gray-200 rounded-xl p-5">
                <div className="flex items-center mb-2">
                  <Phone className="w-5 h-5 text-paybyphone-green mr-2" />
                  <h2 className="text-lg font-semibold text-gray-900">Phone / Téléphone</h2>
                </div>
                <a href={`tel:${SUPPORT_PHONE_TEL}`} className="text-paybyphone-green text-xl font-medium hover:underline">
                  {SUPPORT_PHONE}
                </a>
                <p className="text-sm text-gray-500 mt-1">{SUPPORT_HOURS}</p>
              </div>

              <div className="border border-gray-200 rounded-xl p-5">
                <div className="flex items-center mb-2">
                  <Mail className="w-5 h-5 text-paybyphone-green mr-2" />
                  <h2 className="text-lg font-semibold text-gray-900">Customer Support</h2>
                </div>
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-paybyphone-green text-lg font-medium hover:underline break-all">
                  {SUPPORT_EMAIL}
                </a>
                <p className="text-sm text-gray-500 mt-1">
                  Subscription, billing, refunds, cancellation, technical issues.
                </p>
              </div>

              <div className="border border-gray-200 rounded-xl p-5">
                <div className="flex items-center mb-2">
                  <Mail className="w-5 h-5 text-paybyphone-green mr-2" />
                  <h2 className="text-lg font-semibold text-gray-900">Privacy Requests</h2>
                </div>
                <a href={`mailto:${PRIVACY_EMAIL}`} className="text-paybyphone-green text-lg font-medium hover:underline break-all">
                  {PRIVACY_EMAIL}
                </a>
                <p className="text-sm text-gray-500 mt-1">Data access, deletion, and GDPR-related requests.</p>
              </div>

              <div className="border border-gray-200 rounded-xl p-5">
                <div className="flex items-center mb-2">
                  <MapPin className="w-5 h-5 text-paybyphone-green mr-2" />
                  <h2 className="text-lg font-semibold text-gray-900">Mailing Address</h2>
                </div>
                <address className="not-italic text-gray-700 leading-relaxed">
                  {COMPANY_NAME} — {BRAND_NAME}
                  <br />
                  {COMPANY_ADDRESS_LINE1}
                  <br />
                  {COMPANY_ADDRESS_LINE2}
                </address>
              </div>

              <div className="border border-gray-200 rounded-xl p-5 flex items-start">
                <Clock className="w-5 h-5 text-paybyphone-green mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-1">Business Hours</h2>
                  <p className="text-gray-700 text-sm">{SUPPORT_HOURS}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Legal disclosure */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              {BRAND_NAME} is a trade name of {COMPANY_NAME}. For legal information, refer to our{' '}
              <Link to="/legal" className="text-paybyphone-green underline">Legal Notice</Link>,{' '}
              <Link to="/terms" className="text-paybyphone-green underline">Terms and Conditions</Link> and{' '}
              <Link to="/privacy" className="text-paybyphone-green underline">Privacy Policy</Link>. Website: {WEBSITE_URL}.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
